import { Button, ButtonTypes, ConfirmModal, Icon, IconSize, Select, Switch, Table } from "@clintonelec/react-storybook";
import { type IpFilter, type NetworkSecurityState, IpFilterType } from "Interfaces";
import { memo } from "react";
import FilterManagementModal from "Components/ConfigLayout/Network/SecuritySettings/FilterManagementModal";
import { ipFilterRuleOptions, ipFilterTableColumns } from "Data/Objects/NetworkSecurity";

type FilterActionsProps = {
	disabled: boolean;
	filterActionCallback: (filter: IpFilter | string, oldFilter?: string) => void;
	targetFilter: IpFilter;
};

const FilterActions = memo(function FilterActions(props: FilterActionsProps) {
	const { disabled, filterActionCallback, targetFilter } = props;
	const deleteFilter = () => filterActionCallback(targetFilter.addresses[ 0 ]);
	let filterActionsContent =
		<div className="table-actions">
			<FilterManagementModal
				filter={ targetFilter }
				filterActionCallback={ filterActionCallback }
			>
				<div className="table-icon-button" >
					<Icon name="pencil" size={ IconSize.SMALLER } />
				</div>
			</FilterManagementModal>
			<ConfirmModal
				cancelButton={ { text: "Cancel", type: ButtonTypes.DANGER } }
				modalContent="Are you sure you want to do this? It cannot be undone."
				title="Delete IP Address"
				okButton={ { text: "Confirm", onClick: deleteFilter, type: ButtonTypes.TERTIARY } }
			>
				<div className="table-icon-button" >
					<Icon name="trash" size={ IconSize.SMALLER } />
				</div>
			</ConfirmModal>
		</div>;

	if (disabled) {
		filterActionsContent = <div className="table-actions">
			<div className="table-icon-button icon-disabled" >
				<Icon name="pencil" size={ IconSize.SMALLER } />
			</div>
			<div className="table-icon-button icon-disabled" >
				<Icon name="trash" size={ IconSize.SMALLER } />
			</div>
		</div>;
	}

	return filterActionsContent;
});

type IpFilterTabProps = {
	securitySettings: NetworkSecurityState;
	handleFilterEnabledChange: (checked: boolean) => void;
	handleIpRuleSelect: (value: string | null) => void;
	handleFilterAction: (filter: IpFilter | string, oldFilterAddress?: string) => void;
};

function IpFilterTab(props: IpFilterTabProps) {
	const {
		securitySettings, handleFilterEnabledChange, handleIpRuleSelect, handleFilterAction: updateFilter
	} = props;

	const getIpFilterData = () => securitySettings.ipFiltering.filters?.map(current => {
		const { addresses, type } = current;
		const addressField = `
		${ addresses[ 0 ] }${ type === IpFilterType.ADDRESS_RANGE ? " ~ " + addresses[ 1 ] : "" }
		`;

		return {
			key: addresses[ 0 ],
			ipAddress: addressField,
			action: (
				<FilterActions
					disabled={ !securitySettings.ipFiltering.ipFilterEnabled }
					targetFilter={ current }
					filterActionCallback={ updateFilter }
				/>
			)
		};
	});

	return (
		<div className="ip-filter-tab">
			<h4>IP Address Filtering</h4>
			<div className="form-row switch">
				<span>IP Filter Enable</span>
				<Switch
					checked={ securitySettings.ipFiltering.ipFilterEnabled }
					formName="ipFilterEnabled"
					onChange={ handleFilterEnabledChange }
				/>
			</div>
			<div className="form-row">
				<span>IP Filter Rule</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled={ !securitySettings.ipFiltering.ipFilterEnabled }
					name="ipFilterRule"
					onSelect={ handleIpRuleSelect }
					options={ ipFilterRuleOptions }
					value={ securitySettings.ipFiltering.ipFilterRule }
				/>
			</div>
			<FilterManagementModal
				allFilters={ securitySettings.ipFiltering.filters }
				filterActionCallback={ updateFilter }
			>
				<Button
					className="add-filter"
					disabled={ !securitySettings.ipFiltering.ipFilterEnabled }
					htmlType="button"
					icon={ { name: "plus" } }
					type={ ButtonTypes.TERTIARY }
				>
					Add Address
				</Button>
			</FilterManagementModal>
			<Table
				columns={ ipFilterTableColumns }
				data={ getIpFilterData() ?? [] }
			/>
		</div>
	);
}

export default memo(IpFilterTab);
